import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import dayjs from "dayjs";

// helpers
// @ts-ignore
// import { DayList } from "@/components/_elements/form-items/selects/select-days/select-days.vue";
// enums
export enum DayList {
  Monday = 0,
  Tuesday = 1,
  Wednesday = 2,
  Thursday = 3,
  Friday = 4,
  Saturday = 5,
  Sunday = 6,
}

// get current year
export function currentYear(): string {
  return dayjs().format("YYYY");
}

// get current date without time (2020-01-01)
export function currentDate(): string {
  return dayjs().format("YYYY-MM-DD");
}

// get current date with hours and minutes (2020-01-01 10:55)
export function currentDateTime(): string {
  return dayjs().format("YYYY-MM-DD HH:mm");
}

// get name of the day from the date (Monday, Wednesday and etc)
export function dayName(value: string): string {
  return dayjs(value).format("dddd");
}

// get day number with ordinal from the date (1st, 2nd, 3rd, 4th and etc)
export function ordinalDayOfMonth(value: string): string {
  return dayjs(value).format("Do");
}

// get first two characters of the day name from the date (Mo, We and etc)
export function twoCharacterOfDayName(value: string): string {
  return dayjs(value).format("dd");
}

// get day number of month from the date (1, 2, 3 and etc)
export function dayNumber(value: string): string {
  return dayjs(value).format("D");
}

// get number of day repeat at month from the date (1, 2, 3 and etc)
export function dayRepeatNumberInMonth(value: string): number {
  return dayjs(value).week() - dayjs(value).startOf("month").week() + 1;
}

// get number of days in month from the date (28, 29, 30, 31)
export function daysInMonth(value: string): number {
  return dayjs(value, "YYYY-MM").daysInMonth();
}

// get date on {value} years more than the current witn hours and minutes (2025-01-01 10:55)
export function currentDateWithNextYear(value: number): string {
  return dayjs().add(value, "years").format("YYYY-MM-DD HH:mm");
}

// get date with years more than the current and custom hour and minute (2025-01-01 10:55)
export function currentDateSetYearHourMinute(
  year: number,
  hour: number,
  min: number
) {
  return dayjs()
    .add(year, "years")
    .set("hour", hour)
    .set("minute", min)
    .format("YYYY-MM-DD HH:mm");
}

// get date with slash separator (01/01/2025)
export function dateWithSlash(value: string) {
  return dayjs(value).format("DD/MM/YYYY");
}

// increase custom date to custom values (return date with custom format)
export function increaseDateOn(params: {
  date: any;
  value: any;
  period: any;
  dateFormat?: string;
}): string {
  const { date, value, period, dateFormat = "YYYY-MM-DD" } = params;

  return dayjs(date).add(value, period).format(dateFormat);
}

// decrease custom date to custom values (return date with custom format)
export function decreaseDateOn(params: {
  date: any;
  value: any;
  period: any;
  dateFormat?: string;
}): string {
  const { date, value, period, dateFormat = "YYYY-MM-DD" } = params;

  return dayjs(date).subtract(value, period).format(dateFormat);
}

// get date unix
export function dateUnix(value: string): number {
  return dayjs(value).unix();
}

// get date with hours and minutes (2020-01-01 10:55)
export function dateTimeForTable(date: string | null): string {
  if (!date) {
    return String(t("common.filters.not-specified"));
  }

  [date] = date.split("+");

  return dayjs(date).format("YYYY-MM-DD HH:mm");
}

// get date with dots => 01.01.2021 or Not specified
export function dateWithDots(date: string | null): string {
  if (!date) {
    return String(t("common.filters.not-specified"));
  }

  [date] = date.split("+");

  return dayjs(date).format("DD.MM.YYYY");
}

export function diffDate(
  first: string,
  last: string,
  type: any
): string | number {
  const start = dayjs(first);
  const end = dayjs(last);

  return end.diff(start, type);
}

// get incoming date (2020-01-01 10:55:00) without seconds (2020-01-01 10:55)
export function dateWithoutSeconds(value: string): string {
  return dayjs(value).format("YYYY-MM-DD HH:mm");
}

// get incoming date (2025-12-01 17:07:33) formated (Dec 1st 25 17:07)
export function formatedDate(value: string): string {
  if (!value) return value;

  return dayjs(value).format("MMM Do YY HH:mm");
}

// get incoming date (Sun Sep 27 2020 00:00:00 GMT-0400 (Eastern Daylight Time)) without seconds (2020-01-01)
export function formatedDateAsStartDate(value: Date): string {
  return dayjs(value).format("YYYY-MM-DD");
}

// get incoming date (2025-12-01 17:07:33) without seconds (10:30)
export function formatedTime(value: Date): string {
  return dayjs(value).format("HH:mm");
}

// get incoming date in RFC 3339
export function formatedDateAndTimeToDate(value: string): string {
  return dayjs(value).format();
}

/**
 * Get incoming date (2020-01-01 10:55:00) without seconds (2020-01-01)
 * @param {string} value: incoming date with time
 * @returns {string}
 */
export function dateWithoutTime(value: string): string {
  return dayjs(value).format("YYYY-MM-DD");
}

/**
 * Formatting the incoming date (2021-10-19) to (19 October 2021)
 *
 * @param {string} date: incoming date
 * @returns {string}
 */
export function dateStringMonth(date: string): string {
  return dayjs(date).format("DD MMMM YYYY");
}

/**
 * Formatting the incoming date (2021-10-19) to (Tuesday)
 *
 * @param {string} date: incoming date
 * @returns {string}
 */
export function dateStringDay(date: string): string {
  return dayjs(date).format("dddd");
}

// WITHOUT TIME ZONE
/**
 * Get incoming date (2020-01-01 10:55:00) without seconds (2020-01-01 10:55) in utc
 *
 * @param {string} value: date with seconds
 * @returns {string}
 */
export function utcDateWithoutSeconds(value: string): string {
  return dayjs.utc(value).format("YYYY-MM-DD HH:mm");
}

/**
 * Get start week day ("2022-02-07 00:00")
 *
 * isoWeekday 1 = Monday
 * isoWeekday 7 = Sunday
 * startDayWeek 0 = Monday
 * startDayWeek 6 = Sunday
 *
 * @param {number} startDayWeek: day on which the week will start
 * @returns {string}
 */
export function utcStartWeekDateTime(
  startDayWeek: any = DayList.Monday
): string {
  // moment week 1 - 7, but we have 0 - 6
  const todayNumber = dayjs().isoWeekday() - 1;

  // get day of last week (today is the last day of the week)
  if (startDayWeek > todayNumber) {
    const date = dayjs()
      .subtract(1, "weeks")
      .isoWeekday(startDayWeek + 1)
      .format("YYYY-MM-DD");

    return `${date} 00:00`;
  }

  // get day of current week (week starts with "startDayWeek")
  if (startDayWeek < todayNumber) {
    const date = dayjs()
      .isoWeekday(startDayWeek + 1)
      .format("YYYY-MM-DD");

    return `${date} 00:00`;
  }

  // week starts today
  const date = dayjs.utc().format("YYYY-MM-DD");

  return `${date} 00:00`;
}

/**
 * Get end week day by start week day ("2022-02-13 23:59")
 *
 * @returns {string}
 */
export function utcEndWeekDateTimeByStartDate(startWeek: string): string {
  const date = dayjs.utc(startWeek).add(6, "day").format("YYYY-MM-DD");

  return `${date} 23:59`;
}

/**
 * Increase custom date to custom values (return date with custom format) in utc
 *
 * @param {object} params: object with date, value to increase, period to increase, date format
 * @returns {string}
 */
export function utcIncreaseDateOn(params: {
  date: any;
  value: any;
  period: any;
  dateFormat?: any;
}): string {
  const { date, value, period, dateFormat = "" } = params;

  return dayjs.utc(date).add(value, period).format(dateFormat);
}

/**
 * Decrease custom date to custom values (return date with custom format) in utc
 *
 * @param {object} params: object with date, value to increase, period to increase, date format
 * @returns {string}
 */
export function utcDecreaseDateOn(params: {
  date: any;
  value: any;
  period: any;
  dateFormat?: any;
}): string {
  const { date, value, period, dateFormat = "" } = params;

  return dayjs.utc(date).subtract(value, period).format(dateFormat);
}

/**
 * Get diff for specific case
 *
 * @param {string} end: date
 * @returns{string}
 */
export function diffByEnd(end: string): number {
  const startUtc = dayjs.utc().valueOf();
  const endUtc = dayjs.utc(end).valueOf();

  return (endUtc - startUtc) / 1000;
}

/**
 * Get incoming date in RFC 3339 in utc
 *
 * @param {string} value: date
 * @returns {string}
 */
export function utcFormatedDateAndTimeToDate(value: string): string {
  return dayjs.utc(value).format();
}

/**
 * Get incoming date (2020-01-01 10:55:00) without seconds (2020-01-01) in utc
 *
 * @param {string} value: incoming date with time
 * @returns {string}
 */
export function utcDateWithoutTime(value: string): string {
  return dayjs.utc(value).format("YYYY-MM-DD");
}
